import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  Menu,
  X,
  MessageCircle,
  Rocket,
  Wrench,
  CheckCircle2,
  Clock,
  Sparkles,
  RefreshCw,
  Ban,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, RevealStagger } from "@/components/motion/Reveal";
import logoAsset from "@/assets/flashweb-logo.webp.asset.json";

const WA_URL =
  "https://wa.me/41782369200?text=Ciao%20Filippo%2C%0A%0AHo%20letto%20la%20pagina%20%22Come%20funziona%22%20e%20vorrei%20partire%20con%20il%20mio%20sito.%0A%0ANome%20attivit%C3%A0%3A%0ASettore%3A%0ATelefono%3A%0A%0AAttendo%20il%20tuo%20contatto.";

export const Route = createFileRoute("/come-funziona")({
  head: () => ({
    meta: [
      { title: "Come funziona — Dal briefing al sito online in 48 ore | FlashWeb" },
      {
        name: "description",
        content:
          "Cosa succede esattamente dopo che ordini: il processo passo dopo passo, ora per ora, dal briefing alla pubblicazione.",
      },
      { property: "og:title", content: "Come funziona — FlashWeb Agency" },
      {
        property: "og:description",
        content:
          "Dal briefing alla messa online in 48 ore: cosa fa FlashWeb, cosa serve da te, ora per ora. Nessuna call infinita.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.flash-web.ch/come-funziona" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/come-funziona" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Come funziona FlashWeb: dall'ordine al sito online in 48 ore",
          description:
            "Il processo FlashWeb passo dopo passo: briefing guidato, costruzione del sito, pubblicazione online.",
          totalTime: "PT48H",
          step: [
            {
              "@type": "HowToStep",
              position: 1,
              name: "Ordine e briefing guidato",
              text: "Configuri il sito online e completi un briefing guidato di 5 minuti con testi, foto e riferimenti.",
            },
            {
              "@type": "HowToStep",
              position: 2,
              name: "Costruzione del sito",
              text: "Il team FlashWeb costruisce il sito: design, sviluppo, contenuti, SEO on-page.",
            },
            {
              "@type": "HowToStep",
              position: 3,
              name: "Approvazione e pubblicazione",
              text: "Rivedi il sito, chiedi eventuali modifiche incluse, e pubblichiamo online con dominio e hosting.",
            },
          ],
        }),
      },
          {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.flash-web.ch/" },
            { "@type": "ListItem", position: 2, name: "Come funziona", item: "https://www.flash-web.ch/come-funziona" },
          ],
        }),
      },
],
  }),
  component: ComeFunzionaPage,
});

// ─── Navbar ───
function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border/50 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2 group">
          <img
            src={logoAsset.url}
            alt="FlashWeb Agency"
            width="144"
            height="36"
            className="h-9 w-auto brightness-110 contrast-125 transition-transform group-hover:scale-105"
          />
          <span className="font-display text-lg font-bold tracking-tight">FlashWeb</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="hero" size="default" asChild className="hidden sm:inline-flex fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight />
            </a>
          </Button>
          <Button variant="hero" size="sm" asChild className="sm:hidden fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <button
            type="button"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/60 text-foreground"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/50 bg-background/95 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl flex-col px-4 py-3 text-sm">
            {[
              { href: "/", label: "Home" },
              { href: "/servizi", label: "Servizi" },
              { href: "/prezzi", label: "Prezzi" },
              { href: "/come-funziona", label: "Come funziona" },
              { href: "/portfolio", label: "Portfolio" },
              { href: "/recensioni", label: "Recensioni" },
              { href: "/trasparenza", label: "Trasparenza" },
              { href: "/chi-siamo", label: "Chi siamo" },
              { href: "/faq", label: "FAQ" },
              { href: "/blog", label: "Blog" },
              { href: "/contatti", label: "Contatti" },
              { href: "/portal", label: "Area clienti" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-foreground/90 transition-colors hover:bg-muted hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

// ─── Footer ───
function Footer() {
  return (
    <footer className="border-t border-border bg-card/30">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <img src={logoAsset.url} alt="FlashWeb Agency" width="112" height="28" className="h-7 w-auto" />
              <span className="font-display text-sm font-bold">FlashWeb Agency</span>
            </div>
            <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} — Tutti i diritti riservati.</p>
            <p className="text-sm text-muted-foreground">
              Un brand di <span className="font-semibold text-foreground">FVT FuturaVision Technology GmbH</span>
            </p>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sedi</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><span className="font-semibold text-foreground">Bareerstrasse 75</span><br />6300 Zug, Svizzera</p>
              <p><span className="font-semibold text-foreground">Via Ai Prati 2</span><br />Bedano, Svizzera</p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Contatti</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><a href="mailto:info@flash-web.ch" className="font-semibold text-foreground hover:underline">info@flash-web.ch</a></p>
              <p><a href="https://wa.me/41782369200" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:underline">WhatsApp +41 78 236 92 00</a></p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Informazioni legali</h4>
            <p className="text-sm text-muted-foreground">N° registro commercio:<br /><span className="font-semibold text-foreground">CH-170.4.022.832-9</span></p>
            <div className="flex flex-col gap-1.5 pt-1 text-sm">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link>
              <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground">Cookie Policy</Link>
              <Link to="/termini-condizioni" className="text-muted-foreground hover:text-foreground">Termini e Condizioni</Link>
              <Link to="/impressum" className="text-muted-foreground hover:text-foreground">Impressum</Link>
            </div>
          </div>
        </div>
        <nav aria-label="Naviga" className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/60 pt-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <Link to="/servizi" className="hover:text-foreground">Servizi</Link>
          <Link to="/prezzi" className="hover:text-foreground">Prezzi</Link>
          <Link to="/come-funziona" className="hover:text-foreground">Come funziona</Link>
          <Link to="/portfolio" className="hover:text-foreground">Portfolio</Link>
          <Link to="/recensioni" className="hover:text-foreground">Recensioni</Link>
          <Link to="/trasparenza" className="hover:text-foreground">Trasparenza</Link>
          <Link to="/chi-siamo" className="hover:text-foreground">Chi siamo</Link>
          <Link to="/faq" className="hover:text-foreground">FAQ</Link>
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <Link to="/contatti" className="hover:text-foreground">Contatti</Link>
        </nav>
        <div className="mt-6 flex flex-col gap-2 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} FVT FuturaVision Technology GmbH — Tutti i diritti riservati.</p>
          <p>Realizzato con precisione svizzera 🇨🇭</p>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════ DATA ═══════════════

type Step = {
  num: string;
  when: string;
  emoji: string;
  Icon: typeof Rocket;
  title: string;
  flashweb: string[];
  client: string[];
};

const STEPS: Step[] = [
  {
    num: "01",
    when: "Ora 0",
    emoji: "🚀",
    Icon: Rocket,
    title: "Ordini il sito",
    flashweb: [
      "Ricevi conferma d'ordine e link al briefing guidato",
      "Ti assegniamo un unico referente (chi costruisce, non un filtro)",
      "Preventivo bloccato: nessuna sorpresa in corso d'opera",
    ],
    client: [
      "5 minuti di briefing guidato online (no call di scoperta)",
      "Carichi logo, foto e testi se li hai — altrimenti li scriviamo noi",
      "Indichi 2-3 siti che ti piacciono come riferimento",
    ],
  },
  {
    num: "02",
    when: "Ora 4 – 40",
    emoji: "🛠️",
    Icon: Wrench,
    title: "Lo costruiamo",
    flashweb: [
      "Design su misura basato sul briefing (nessun template pronto)",
      "Sviluppo, integrazione contenuti, SEO on-page, ottimizzazione mobile",
      "Test velocità, accessibilità e compatibilità cross-browser",
    ],
    client: [
      "Niente. Lavori serenamente al tuo business",
      "Ti scriviamo solo se manca un dato critico (dominio, email)",
    ],
  },
  {
    num: "03",
    when: "Ora 48",
    emoji: "✅",
    Icon: CheckCircle2,
    title: "Lo pubblichiamo",
    flashweb: [
      "Ti mandiamo l'anteprima privata: navighi, provi, valuti",
      "Applichiamo la revisione inclusa (se ne hai bisogno)",
      "Configuriamo dominio, hosting, email e certificato SSL",
    ],
    client: [
      "Approvi l'anteprima o segnali le modifiche incluse",
      "Ci consegni il dominio (o lo compriamo noi per te)",
    ],
  },
];

type PlanTime = {
  name: string;
  time: string;
  emoji: string;
  Icon: typeof Clock;
  changes: string;
  href: string;
};

const PLAN_TIMES: PlanTime[] = [
  {
    name: "Sito Professionale",
    time: "48 ore",
    emoji: "⚡",
    Icon: Clock,
    changes: "Processo standard: briefing → costruzione → pubblicazione. 1 revisione inclusa.",
    href: "/servizi",
  },
  {
    name: "Sito Completo",
    time: "72 ore",
    emoji: "✨",
    Icon: Sparkles,
    changes: "Fase design estesa per animazioni, integrazioni e fino a 5 pagine. 2 revisioni incluse.",
    href: "/servizi",
  },
  {
    name: "FlashWeb Flex",
    time: "Stessa settimana",
    emoji: "🔄",
    Icon: RefreshCw,
    changes: "Attivazione in 3-5 giorni + iterazioni continue: aggiornamenti illimitati inclusi nell'abbonamento.",
    href: "/servizi",
  },
];

const NOT_DOING = [
  {
    title: "Niente preventivi a sorpresa",
    body: "Il prezzo che leggi è quello che paghi. Zero costi nascosti a metà progetto.",
  },
  {
    title: "Niente call di scoperta di un'ora",
    body: "5 minuti di briefing guidato online. Se serve un chiarimento, ti scriviamo su WhatsApp.",
  },
  {
    title: "Niente project manager filtro",
    body: "Parli direttamente con chi costruisce il sito. Zero telefono senza fili.",
  },
  {
    title: "Niente contratti annuali forzati",
    body: "Sito una tantum: paghi e sei tuo. Flex: disdici quando vuoi, senza penali.",
  },
];

const PROCESS_FAQS = [
  {
    q: "Cosa succede se ho ancora modifiche dopo la revisione inclusa?",
    a: "Le revisioni extra costano CHF 30–90 a seconda della complessità. Su Flex sono tutte incluse.",
  },
  {
    q: "Posso vedere il sito prima che sia online?",
    a: "Sì. Alla fine dell'ora 48 ricevi un link privato all'anteprima: navighi, provi, poi approvi o segnali modifiche.",
  },
  {
    q: "Cosa succede se le 48 ore non bastano?",
    a: "Consegna garantita: se sforiamo per colpa nostra, applichiamo uno sconto proporzionale (dettagli in FAQ).",
  },
  {
    q: "Devo scrivere io i testi del sito?",
    a: "No. Se non li hai o non hai voglia, li scriviamo noi partendo dal briefing di 5 minuti.",
  },
];

// ═══════════════ PAGE ═══════════════

function ComeFunzionaPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* 1. HERO */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-4xl px-6 py-24 text-center sm:py-32">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
              Come funziona
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
              Dall'ordine al sito online.{" "}
              <span className="text-gradient inline-block">Ora per ora.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground sm:text-xl">
              Nessuna call infinita, nessun brief di 10 pagine. Ecco esattamente cosa succede dopo che
              premi <span className="font-semibold text-foreground">"Configura il tuo sito"</span>.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="hero" size="xl" asChild className="fw-shimmer">
                <Link to="/">
                  Configura il tuo sito <ArrowRight />
                </Link>
              </Button>
              <Button variant="outlineHero" size="xl" asChild>
                <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle /> Scrivici su WhatsApp
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2. TIMELINE 3 STEP */}
      <section className="relative border-b border-border/60 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Il processo</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Tre step. Zero ambiguità.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Cosa fa FlashWeb e cosa serve da te, per ognuna delle 48 ore.
              </p>
            </div>
          </Reveal>

          {/* Timeline: vertical on mobile, horizontal on desktop */}
          <div className="relative mt-16">
            {/* Connector line — vertical on mobile */}
            <div
              aria-hidden
              className="absolute left-6 top-0 h-full w-px bg-gradient-to-b from-primary/60 via-primary/30 to-transparent md:hidden"
            />
            {/* Connector line — horizontal on desktop */}
            <div
              aria-hidden
              className="absolute left-0 right-0 top-8 hidden h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent md:block"
            />

            <RevealStagger className="grid gap-8 md:grid-cols-3 md:gap-6">
              {STEPS.map((step) => (
                <StepCard key={step.num} step={step} />
              ))}
            </RevealStagger>
          </div>
        </div>
      </section>

      {/* 3. VARIANTE PER PIANO */}
      <section className="relative border-b border-border/60 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Tempi per piano</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Ogni piano, il suo ritmo.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Il processo cambia leggermente in base al piano scelto. Ecco come.
              </p>
            </div>
          </Reveal>

          <RevealStagger className="mt-14 grid gap-6 md:grid-cols-3">
            {PLAN_TIMES.map((p) => (
              <div
                key={p.name}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card/40 p-6 backdrop-blur transition-all hover:border-primary/50 hover:bg-card/60"
              >
                <div className="flex items-center justify-between">
                  <span className="text-2xl" aria-hidden>{p.emoji}</span>
                  <p.Icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold">{p.name}</h3>
                <p className="mt-1 text-2xl font-black text-gradient">{p.time}</p>
                <p className="mt-4 text-sm text-muted-foreground">{p.changes}</p>
                <Link
                  to={p.href}
                  className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                >
                  Vedi il piano <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* 4. COSA NON FACCIAMO */}
      <section className="relative border-b border-border/60 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Chiarezza</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Cosa <span className="text-gradient">non</span> facciamo.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Perché "veloce e a tariffa fissa" funziona solo se togli quello che non serve.
              </p>
            </div>
          </Reveal>

          <RevealStagger className="mt-14 grid gap-4 sm:grid-cols-2">
            {NOT_DOING.map((item) => (
              <div
                key={item.title}
                className="flex gap-4 rounded-2xl border border-border bg-card/40 p-6 backdrop-blur"
              >
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-border bg-background">
                  <Ban className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-bold">{item.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{item.body}</p>
                </div>
              </div>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* 5. FAQ SUL PROCESSO */}
      <section className="relative border-b border-border/60 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Domande sul processo</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Le domande che tutti fanno.
              </h2>
            </div>
          </Reveal>

          <div className="mt-10 space-y-3">
            {PROCESS_FAQS.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} />
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/faq"
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              Vedi tutte le FAQ <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 6. CTA FINALE */}
      <section className="relative overflow-hidden py-20 sm:py-28">
        <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
              Pronto a partire?{" "}
              <span className="text-gradient inline-block">48 ore da adesso.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground sm:text-lg">
              Configura il tuo sito online, oppure scrivici su WhatsApp: rispondiamo in giornata.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="hero" size="xl" asChild className="fw-shimmer">
                <Link to="/">
                  Configura il tuo sito <ArrowRight />
                </Link>
              </Button>
              <Button variant="outlineHero" size="xl" asChild>
                <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle /> Scrivici su WhatsApp
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// ─── Step card ───
function StepCard({ step }: { step: Step }) {
  const { num, when, emoji, Icon, title, flashweb, client } = step;
  return (
    <div className="relative pl-16 md:pl-0">
      {/* Node marker — left on mobile, top-center on desktop */}
      <div className="absolute left-0 top-0 grid h-12 w-12 place-items-center rounded-full border border-primary/40 bg-background shadow-[0_0_20px_color-mix(in_oklab,var(--primary)_30%,transparent)] md:relative md:mx-auto md:mb-6">
        <span className="font-display text-sm font-black text-primary">{num}</span>
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-border bg-card/40 p-6 backdrop-blur transition-all hover:border-primary/40">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-semibold text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            {when}
          </div>
          <span className="text-2xl" aria-hidden>{emoji}</span>
        </div>

        <h3 className="mt-4 font-display text-xl font-bold sm:text-2xl">{title}</h3>

        <div className="mt-5 space-y-4 text-sm">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary">Cosa fa FlashWeb</p>
            <ul className="mt-2 space-y-1.5">
              {flashweb.map((f, i) => (
                <li key={i} className="flex gap-2 text-muted-foreground">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border-t border-border/60 pt-4">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Cosa serve da te</p>
            <ul className="mt-2 space-y-1.5">
              {client.map((c, i) => (
                <li key={i} className="flex gap-2 text-muted-foreground">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-foreground/60" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FAQ item ───
function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card/40 backdrop-blur">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="font-display text-base font-semibold">{q}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-border/60 px-5 py-4 text-sm text-muted-foreground">{a}</div>
      )}
    </div>
  );
}
